import { cloneDeep, Dictionary, isEqual } from "lodash";
import { Moment } from "moment";
import { CharDataModifyAction } from "../../../models/char-data-modify-action";
import { DateMathChanges } from "../../../models/date-math-changes";
import { ICharacteristicData } from "../../../models/i-characteristic-data";
import { ICharacteristicMetaData } from "../../../models/i-characteristic-meta-data";
import { ICharacteristicMetaDataCollection } from "../../../models/i-characteristic-meta-data-collection";
import { ICharacteristicMetaDataValue } from "../../../models/i-characteristic-meta-data-value";
import { SystemIndicators } from "../../../rl-common.consts";
import { ISaveCharDataRequest } from "../../../services/entity/models/i-save-char-data-request";
import { OneConfigService } from "../../../services/one-config/one-config.service";
import { CharacteristicUtil, TagLabel } from "../../../utils/characteristic.util";
import { DateLocaleType, DateUtil } from "../../../utils/date.util";
import { JSONUtil } from "../../../utils/json.util";
import { BulkEditsPackage } from "../../bulk-grid/bulk-edit.models";
import { ElementsUtil } from "../../char-data/elements/elements.util";

export class EditEntityUtils {

	private static getLocMoney(val?: string) {
		if (!val) return null;
		const obj = JSONUtil.parse<{ locAmt?: number; locCur?: string; locSym?: string }>(val);
		if (!obj) return null;
		return {
			locAmt: obj?.locAmt ?? null,
			locCur: obj?.locCur ?? null,
			locSym: obj?.locSym ?? null
		};
	}
	// // TODO: Do we still need this method?
	// // Look at makeCharDataBulkEditPackage() in entity-chardata-data-change.strategy.ts, at some point we should share this logic.
	public static getEditedCharData(_oneConfigService: OneConfigService, allInitialCharData: ICharacteristicData[], allNewCharData: ICharacteristicData[], cmds: ICharacteristicMetaData[]): ISaveCharDataRequest {
		allInitialCharData = allInitialCharData ?? [];
		allNewCharData = allNewCharData ?? [];
		const deletedRecCharIds: number[] = [];
		const editedCharData: ICharacteristicData[] = [];
		const deletedDateMathCalcIds: number[] = [];
		const deletedDateMathRelIds: number[] = [];

		// process column edits and lov merging
		cmds.forEach(cmd => {
			const charId = cmd.characteristicID;
			const newCharData = allNewCharData.filter(x => x.charactersticID === charId);
			const oldCharData = allInitialCharData.filter(x => x.charactersticID === charId);
			const lovMetaData = _oneConfigService.getLovMetaData(cmd.charValueSourceID);
			let lovs = lovMetaData?.listOfValues;
			if (lovMetaData?.inactiveListOfValues !== undefined) {
				lovs = lovs.concat(lovMetaData?.inactiveListOfValues.map(x => x.cmdv));
			}

			let mergedCharData: ICharacteristicData[] = cloneDeep(oldCharData);
			mergedCharData = CharacteristicUtil.modifyCharData(cmd, mergedCharData, CharDataModifyAction.Replace, newCharData, lovs);

			if (!mergedCharData) {
				return;
			}

			// don't create empty char data when the user didn't even edit the field
			if (oldCharData.length === 0 && newCharData.length === 1 && !newCharData[0].value && !newCharData[0].valueID && !newCharData[0].ext) {
				return;
			}

			// don't update chars if there wasn't any changes
			const editElementType = ElementsUtil.mapToEditElement(cmd, true, true);
			let valuesEqual = false;
			if (mergedCharData.length === 1 && oldCharData.length === 1) {
				valuesEqual = mergedCharData[0].value === oldCharData[0].value;
				if (editElementType === "money") {
					const newLoc = EditEntityUtils.getLocMoney(mergedCharData[0].value);
					const oldLoc = EditEntityUtils.getLocMoney(oldCharData[0].value);
					valuesEqual = isEqual(newLoc, oldLoc);
				}
			}
			if (mergedCharData.length === 1 && oldCharData.length === 1 &&
				valuesEqual &&
				(mergedCharData[0].valueID || null) === (oldCharData[0].valueID || null) && // (|| null) is to fix instance where null !== undefined.  thank you javascript
				isEqual(mergedCharData[0].ext, oldCharData[0].ext)) {
				return;
			}

			let recCharsToCreate = mergedCharData;
			if (cmd.multipleIndicator <= 0) {
				recCharsToCreate = mergedCharData.filter(x => {
					const isValueUpdated = !oldCharData?.find(y => y.valueID === x.valueID && y.value === x.value);
					const dateMathChanges = CharacteristicUtil.getDateMathChanges(x.ext?.dateMath, [DateMathChanges.Created, DateMathChanges.Updated]);
					const dateMathDeletes = CharacteristicUtil.getDateMathChanges(x.ext?.dateMath, [DateMathChanges.Deleted]);

					return isValueUpdated ||
						dateMathChanges.calcChanges.length > 0 ||
						dateMathChanges.relChanges.length > 0 ||
						dateMathDeletes && x.value; // if we remove datemath calc but there's still a date filled in
				});
			}

			const recCharsToDelete = CharacteristicUtil.getCharsToDelete(mergedCharData, recCharsToCreate, cmd, oldCharData);
			editedCharData.push(...recCharsToCreate);
			deletedRecCharIds.push(...recCharsToDelete.map(x => x.recordCharacteristicID).filter(x => recCharsToCreate.map(y => y.recordCharacteristicID).indexOf(x) === -1));
			if (recCharsToDelete && (cmd.multipleIndicator > 0 || editElementType === "dropdown" || editElementType === "money" || editElementType === "checkbox")) {
				recCharsToDelete.forEach(rc => {
					if (recCharsToCreate.filter(x => x.recordCharacteristicID === rc.recordCharacteristicID).length === 0) {
						rc.value = "";
						rc.valueID = undefined;
					}
				});
				editedCharData.push(...recCharsToDelete);
			}
			const dateMathToDelete = mergedCharData.length > 0 ? CharacteristicUtil.getDateMathChanges(mergedCharData[0].ext?.dateMath, [DateMathChanges.Deleted]) : null;
			deletedDateMathCalcIds.push(...(dateMathToDelete ? dateMathToDelete.calcChanges : []));
			deletedDateMathRelIds.push(...(dateMathToDelete ? dateMathToDelete.relChanges : []));
		});

		return {
			charDatas: editedCharData,
			deletedRecCharIds,
			deletedDateMathCalcIds,
			deletedDateMathRelIds
		} as ISaveCharDataRequest;
	}

	/** Header chars (e.g. a title configured as a picklist) are stored as text only, so match the label to preselect the value */
	public static resolveHeaderLovValueIds(_oneConfigService: OneConfigService, charData: ICharacteristicData[], cmds: ICharacteristicMetaData[]): ICharacteristicData[] {
		return charData?.map(cd => {
			if (cd.recordCharacteristicID !== -1 || cd.valueID || !cd.value) {
				return cd;
			}
			const cmd = cmds.find(x => x.characteristicID === cd.charactersticID);
			const lovMetaData = cmd?.charValueSourceID ? _oneConfigService.getLovMetaData(cmd.charValueSourceID) : null;
			// a saved title keeps its text after its value is deactivated; active values win on a label clash
			const lovs = [
				...EditEntityUtils.flattenLovs(lovMetaData?.listOfValues ?? []),
				...EditEntityUtils.flattenLovs((lovMetaData?.inactiveListOfValues ?? []).map(x => x.cmdv).filter(x => !!x))
			];
			const label = cd.value.trim().toLowerCase();
			const lov = lovs.find(x => x.characteristicValueLabel?.trim().toLowerCase() === label);
			return lov ? { ...cd, valueID: lov.characteristicValueID } : cd;
		});
	}

	private static flattenLovs(lovs: ICharacteristicMetaDataValue[]): ICharacteristicMetaDataValue[] {
		return lovs.flatMap(x => [x, ...EditEntityUtils.flattenLovs(x.childValues ?? [])]);
	}

	public static getChanges(recordId: number, modifiedCharDatas: ICharacteristicData[], cmds: ICharacteristicMetaData[], tempId: number = null): BulkEditsPackage<ICharacteristicData[]> {
		if (!cmds) {
			return;
		}
		const rowEdits: Dictionary<Dictionary<ICharacteristicData[]>> = {};
		rowEdits[recordId] = {};
		cmds.forEach(cmd => {
			rowEdits[recordId][cmd.characteristicID] = modifiedCharDatas.filter(cd => cd.charactersticID === cmd.characteristicID);
		})
		return {
			columnEditDateMathTempId: tempId,
			rowEdits: rowEdits,
			columnEdits: [],
			extraColumnEdits: [],
			extraRowEdits: {}
		};
	}

	/**
	 * Get the actual date from the saved value.
	 */
	private static resolveStorageDate(value: string): Moment {
		if (!value || DateUtil.isTextModeDate(value) || DateUtil.isPerpetuity(DateUtil.normalizeStorageDate(value))) {
			return null;
		}
		const m = DateUtil.parseToUTCMoment(value, DateLocaleType.Storage);
		if (!m.isValid() || m.isSame(DateUtil.BLANK_DATE_MOMENT, "day")) {
			return null;
		}
		return m;
	}

	private static getAmortizationStartDateState(template: ICharacteristicMetaDataCollection, initialCharData: ICharacteristicData[], charData: ICharacteristicData[]) {
		const amortStartCmd = template?.characteristicMetaDatas?.find(cmd => cmd.systemIndicator === SystemIndicators.AmortizationStartDate);
		if (!amortStartCmd) {
			return null;
		}

		const newCd = charData?.find(cd => cd.charactersticID === amortStartCmd.characteristicID);
		const oldCd = initialCharData?.find(cd => cd.charactersticID === amortStartCmd.characteristicID);
		const touched = (newCd?.value || null) !== (oldCd?.value || null) ||
			!isEqual(newCd?.ext?.dateMath, oldCd?.ext?.dateMath) ||
			!isEqual(newCd?.ext?.dateOffset, oldCd?.ext?.dateOffset);

		const isBlank = !newCd?.value || DateUtil.parseToUTCMoment(newCd.value, DateLocaleType.Storage).isSame(DateUtil.BLANK_DATE_MOMENT, "day");
		const cleared = isBlank && !newCd?.ext?.dateMath?.calcs?.length && !newCd?.ext?.dateOffset;
		return {
			touched,
			cleared,
			newDate: EditEntityUtils.resolveStorageDate(newCd?.value),
			oldDate: EditEntityUtils.resolveStorageDate(oldCd?.value)
		};
	}

	/**
	 * Return the updated Amortization Start Date if it has changed otherwise return no date
	 */
	public static getChangedAmortizationStartDate(template: ICharacteristicMetaDataCollection, initialCharData: ICharacteristicData[], charData: ICharacteristicData[]): string {
		const state = EditEntityUtils.getAmortizationStartDateState(template, initialCharData, charData);
		if (!state?.touched || !state.newDate || (state.oldDate && state.newDate.isSame(state.oldDate, "day"))) {
			return null;
		}
		return DateUtil.formatUTCMomentAsDate(state.newDate, DateLocaleType.Storage);
	}

	/**
	 * Return an error if the updated Amortization Start Date is invalid or falls outside the license period.
	 */
	public static validateAmortizationStartDate(template: ICharacteristicMetaDataCollection, initialCharData: ICharacteristicData[], charData: ICharacteristicData[]): string {
		const state = EditEntityUtils.getAmortizationStartDateState(template, initialCharData, charData);
		if (!state?.touched || state.cleared) {
			return null;
		}
		if (!state.newDate) {
			return "Amortization Start Date must resolve to a valid date.";
		}
		if (state.oldDate && state.newDate.isSame(state.oldDate, "day")) {
			return null;
		}

		const licenseStart = EditEntityUtils.resolveStorageDate(CharacteristicUtil.getCharacteristic(TagLabel.LicenseStartDate, template, charData)?.value);
		const licenseEnd = EditEntityUtils.resolveStorageDate(CharacteristicUtil.getCharacteristic(TagLabel.LicenseEndDate, template, charData)?.value);
		if (licenseStart && state.newDate.isBefore(licenseStart, "day")) {
			return "Amortization Start Date must be on or after the License Start Date.";
		}
		if (licenseEnd && !state.newDate.isBefore(licenseEnd, "day")) {
			return "Amortization Start Date must be at least one day before the License End Date.";
		}
		return null;
	}
}